/**
 * @file ReportInstructionsPage.tsx
 * @brief Super Admin → Report instructions.
 *
 * @details
 * One box per MLR section, read before every report is generated from a
 * consultation. The advice differs per section — §4 wants "never merge neck
 * and back into one row", §8 wants how to phrase an outlook — and a single
 * shared blob would send all of it to all of them, making every prompt longer
 * and vaguer. Two boxes are the exception, applied on top of every section:
 * `Before you begin` is what to WEIGH (standing checks that often will not
 * apply to a given claimant) and `All sections` is how to WRITE (house style).
 * They are read in that order, and kept apart because a caution buried in the
 * style box goes unread while style in the cautions box lengthens every prompt.
 *
 * Three things on this page are load-bearing rather than decorative:
 *
 * - **Super admin only.** This is how the product writes expert evidence, not
 *   a tenant preference.
 * - **Saving publishes a version, it does not overwrite.** An MLR is evidence;
 *   "what was the model told when this was written?" must still have an answer
 *   after the text is edited, so each generation stamps the versions it used.
 * - **The limits are stated on the page.** Instructions are appended to the
 *   extractor prompts, never substituted into them, so they can shape wording
 *   and emphasis but cannot make the model state something the consultation
 *   does not support. An admin who does not know that writes rules that
 *   silently do nothing.
 *
 * @references
 * - BACKEND/src/features/medical-assessment/{housePolicy.mjs,mlrInstructions.service.mjs}
 * - docs/decisions/ADR-026-medical-assessment-module.md
 *
 * @author DES
 * @date 14-09-2026
 */

import { useEffect, useMemo, useState } from "react";
import { LoaderCircle, Save } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/feedback/ErrorState";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { formatDateTime } from "@/shared/utils/formatters";
import { cn } from "@/shared/theme/cn";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { useMlrInstructions, useSaveMlrInstructions } from "./api";
import { MlrRulebookPanel } from "./MlrRulebookPanel";

const MAX_CHARS = 6000;

const PLACEHOLDER: Record<string, string> = {
  considerations:
    "Standing things to weigh on every report, whether or not they apply to " +
    "this claimant. For example:\n\nIf a previous accident is mentioned, say " +
    "whether this one made those symptoms worse.\nIf an injury was reported " +
    "but not examined, say so rather than leaving the section silent.\nNever " +
    "describe treatment as ongoing unless the claimant said it is.",
  general:
    "Rules that apply to every section. For example:\n\nRefer to the claimant by title and surname, never by first name.\nWrite in the third person and in the past tense.",
  symptoms:
    "For example:\n\nRecord every injury as its own row — never merge neck and back into one.\nWhere the claimant gives a severity, keep their own word for it.",
  accident:
    "For example:\n\nAlways state whether a seatbelt was worn.\nName the vehicle position the claimant occupied.",
  treatment:
    "For example:\n\nList each treating provider separately with its date.\nSay explicitly when no treatment was sought.",
  pmh: "For example:\n\nIf a pre-existing complaint of the same body part is mentioned, say so explicitly.",
  work: "For example:\n\nWhere time off work is reported, capture the exact number of days.",
  travel:
    "For example:\n\nRecord travel anxiety separately from physical symptoms.",
  homelife:
    "For example:\n\nName the specific activities the claimant says they could not do.",
  examination:
    "For example:\n\nWhere the doctor described a movement without giving a figure, keep the description and leave the percentage out.\nName the side for every limb finding.",
  prognosis:
    "For example:\n\nFor whiplash injuries, recommend physiotherapy unless the doctor says otherwise.\nNever state a recovery period the doctor did not give.",
};

export function ReportInstructionsPage() {
  const instructions = useMlrInstructions();
  const saveInstructions = useSaveMlrInstructions();
  // Opens on the first box in the list, which is the one read first.
  const [activeSection, setActiveSection] = useState("considerations");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loadedKey, setLoadedKey] = useState("");

  const sections = useMemo(
    () => instructions.data?.sections ?? [],
    [instructions.data],
  );
  const stored = useMemo(
    () => instructions.data?.instructions ?? {},
    [instructions.data],
  );
  const versions = instructions.data?.versions ?? [];

  // Reload only when the published set actually changes, so typing in one
  // section is never wiped by a background refetch of another.
  useEffect(() => {
    if (!instructions.data) return;
    const key = Object.entries(stored)
      .map(([section, row]) => `${section}:${row?.version ?? 0}`)
      .sort()
      .join("|");
    if (key === loadedKey) return;
    const next: Record<string, string> = {};
    for (const section of sections) {
      next[section.key] = stored[section.key]?.body ?? "";
    }
    setDrafts(next);
    setLoadedKey(key);
  }, [instructions.data, stored, sections, loadedKey]);

  const activeMeta = sections.find((section) => section.key === activeSection);
  const body = drafts[activeSection] ?? "";
  const publishedBody = stored[activeSection]?.body ?? "";
  const dirty = body !== publishedBody;
  const tooLong = body.length > MAX_CHARS;
  const sectionVersions = versions.filter(
    (version) => version.section === activeSection,
  );

  const onSave = async () => {
    try {
      const saved = await saveInstructions.mutateAsync({
        section: activeSection,
        body,
      });
      toast({
        title: `Published version ${saved.version}`,
        description: "Reports generated from now on will follow these rules.",
      });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Save failed";
      toast({ title: "Could not publish instructions", description: message });
    }
  };

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand className="py-3">
          <h2 className="text-xl font-semibold tracking-tight">
            Report instructions
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Standing rules per report section, read before every report is
            generated from a consultation. Publishing creates a new version —
            earlier ones are kept, and each report records which versions wrote
            it.
          </p>
        </GridBand>

        {instructions.isLoading ? (
          <GridCell className="lg:col-span-2">
            <Skeleton className="h-96 w-full" />
          </GridCell>
        ) : instructions.isError ? (
          <GridCell className="lg:col-span-2">
            <ErrorState
              title="Could not load instructions"
              error={instructions.error ?? "Failed"}
              onRetry={() => void instructions.refetch()}
            />
          </GridCell>
        ) : (
          <GridCell padded={false} className="lg:col-span-2">
            {/* One cell, split by hand: the section list has to sit BESIDE the
                editor, and leaving it to the page grid stacked them — nine
                rows of nav above a text box reads as a menu, not an editor. */}
            <div className="grid lg:grid-cols-[minmax(200px,260px)_1fr]">
              <nav className="border-b border-border/60 p-2 lg:border-b-0 lg:border-r">
                <ul className="space-y-0.5">
                  {sections.map((section) => {
                    const isActive = section.key === activeSection;
                    const edited =
                      (drafts[section.key] ?? "") !==
                      (stored[section.key]?.body ?? "");
                    const published = stored[section.key];
                    return (
                      <li key={section.key}>
                        <button
                          type="button"
                          onClick={() => setActiveSection(section.key)}
                          aria-current={isActive ? "true" : undefined}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                            isActive
                              ? "bg-muted font-medium text-foreground"
                              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                          )}
                        >
                          {section.number ? (
                            <span className="w-11 shrink-0 tabular-nums text-xs text-muted-foreground">
                              {section.number}
                            </span>
                          ) : (
                            <span className="w-11 shrink-0" />
                          )}
                          <span className="min-w-0 flex-1 truncate">
                            {section.label}
                          </span>
                          {edited ? (
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                              aria-label="Unpublished changes"
                            />
                          ) : published ? (
                            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                              v{published.version}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="min-w-0 p-4 md:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold tracking-tight">
                      {activeMeta?.number ? `${activeMeta.number} ` : ""}
                      {activeMeta?.label ?? activeSection}
                    </h3>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {activeMeta?.hint ??
                        "Applied when this section is written."}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {stored[activeSection] ? (
                      <Badge variant="secondary">
                        Version {stored[activeSection]?.version}
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not set</Badge>
                    )}
                    <Button
                      size="sm"
                      onClick={() => void onSave()}
                      disabled={!dirty || tooLong || saveInstructions.isPending}
                    >
                      {saveInstructions.isPending ? (
                        <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Publish
                    </Button>
                  </div>
                </div>

                <textarea
                  aria-label={`Instructions for ${activeMeta?.label ?? activeSection}`}
                  value={body}
                  onChange={(event) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [activeSection]: event.target.value,
                    }))
                  }
                  rows={14}
                  spellCheck
                  placeholder={PLACEHOLDER[activeSection] ?? ""}
                  className="mt-4 w-full resize-y rounded-lg border border-input bg-field px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
                />

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <p className="max-w-lg text-muted-foreground">
                    These rules shape <strong>wording, emphasis and what to
                    capture</strong>. They cannot make a report state something
                    the consultation does not support.
                  </p>
                  <span
                    className={
                      tooLong ? "text-destructive" : "text-muted-foreground"
                    }
                  >
                    {body.length} / {MAX_CHARS}
                  </span>
                </div>

                {sectionVersions.length > 0 ? (
                  <div className="mt-5 border-t border-border/60 pt-3">
                    <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                      Published versions
                    </p>
                    <ul className="divide-y divide-border/60 text-sm">
                      {sectionVersions.map((version) => (
                        <li
                          key={version.id}
                          className="flex items-baseline justify-between gap-2 py-1.5"
                        >
                          <span>Version {version.version}</span>
                          <span className="text-xs text-muted-foreground">
                            {version.createdAt
                              ? formatDateTime(String(version.createdAt))
                              : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
          </GridCell>
        )}

        {/*
            The rulebook, under the instructions and deliberately separate.

            ⚠️ Instructions shape how a section is WRITTEN; rules decide what
            APPLIES. The two look similar on a settings page and behave
            nothing alike: prose in a prompt was broken by a contrary
            instruction 3 runs out of 3 on four separate measurements, while a
            rule is data the code evaluates. Putting them in one box would
            invite writing a tariff rule as a sentence. (18-09-2026)
         */}
        <GridCell className="col-span-12">
          <div className="border-t border-border/60 pt-6">
            <MlrRulebookPanel />
          </div>
        </GridCell>
      </GridBlock>
    </div>
  );
}
